import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { PaymentMethod } from '@stripe/stripe-js';

import { showSnackbar } from 'helpers/snackbar';

import { media } from 'context';
import { Button, Clickable, Icon, Layout, Modal, PillButton, Text, TextButton } from 'ds';
import { TENANT_ADMIN_PAYMENT_METHODS_PATH } from 'routes';
import { actions } from 'store/Tenancy';
import { selectPaymentMethods } from 'store/Tenancy/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';
import { updateTenancyPaymentMethodIds } from 'ux/Tenant/TenantAdmin/requests';

import SelectPaymentMethodCard from './SelectPaymentMethodCard';

import { ReactComponent as StripeBank } from 'ds/StripeBank.svg';

interface UpdateStripePaymentMethodModalProps {
  isVisible: boolean;
  type: 'services' | 'rent' | null;
  onClose: () => void;
  onSelectAddNewPaymentMethod: () => void;
  setSelectedPaymentMethod: (paymentMethod: PaymentMethod | null) => void;
  selectedPaymentMethod: PaymentMethod | null;
  showManagePaymentMethods?: boolean;
}

const UpdateStripePaymentMethodModal: React.FC<UpdateStripePaymentMethodModalProps> = ({
  isVisible,
  type,
  onClose,
  onSelectAddNewPaymentMethod,
  setSelectedPaymentMethod,
  selectedPaymentMethod,
  showManagePaymentMethods
}) => {
  const { isMobile } = useContext(media);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const tenancyId = useAppSelector(selectTenancyId);
  const paymentMethods = useAppSelector(selectPaymentMethods);
  const bankPaymentMethods = paymentMethods?.filter(
    (method: PaymentMethod) => method.us_bank_account && !method.metadata['rent_payment_method']
  );

  const nonServicesPaymentMethods = paymentMethods?.filter(method => !method.metadata['services_payment_method']);

  const handleSave = async () => {
    if (!tenancyId || !selectedPaymentMethod) return;
    try {
      const response = await updateTenancyPaymentMethodIds({
        ...(type === 'services'
          ? { paymentMethodIdServices: selectedPaymentMethod.id }
          : { paymentMethodIdRent: selectedPaymentMethod.id }),
        tenancyId
      });

      if (response.status === 200) {
        showSnackbar({
          message: type === 'services' ? 'Payment method assigned to services' : 'Payment method assigned to rent'
        });
        dispatch(actions.setPaymentMethods(response.data));

        const servicesPaymentMethod = response.data.find(
          (paymentMethod: PaymentMethod) => paymentMethod.metadata.services_payment_method
        );

        const rentPaymentMethod = response.data.find(
          (paymentMethod: PaymentMethod) => paymentMethod.metadata.rent_payment_method
        );

        dispatch(actions.setServicesPaymentMethod(servicesPaymentMethod));
        dispatch(actions.setRentPaymentMethod(rentPaymentMethod));
        onClose();
      }
    } catch (error) {
      console.error(error);
      showSnackbar({
        message: 'Failed to update payment method',
        negative: true
      });
    }
  };

  const mobileModalGutterTop =
    (type === 'rent' && bankPaymentMethods && bankPaymentMethods?.length < 3) ||
    (type === 'services' && nonServicesPaymentMethods && nonServicesPaymentMethods?.length < 3)
      ? 183
      : undefined;

  return (
    <Modal
      bottomSheetGutterTop={mobileModalGutterTop}
      bottomSheetIsRounded={isMobile}
      isBottomSheet={isMobile}
      zIndexPosition={1}
      width={500}
      paddingTop={0}
      isVisible={isVisible}
      onClose={onClose}
      headerAlign="left"
      header={<Text size="headline-xs">Add payment method</Text>}
      footer={
        isMobile ? (
          <PillButton
            fullWidth
            type="primary"
            text="Save changes"
            size="lg"
            onClick={handleSave}
            disabled={!selectedPaymentMethod}
          />
        ) : (
          <Layout direction="row" gap={12} justify="flex-end">
            <Button size="sm" text="Cancel" type="secondary" onClick={onClose} />
            <Button size="sm" text="Save" type="primary" onClick={handleSave} disabled={!selectedPaymentMethod} />
          </Layout>
        )
      }
    >
      <Layout direction="column" gap={12} paddingX={isMobile ? 24 : 0}>
        {type === 'rent' && <Text size="body-sm">A bank account is required for rent payments.</Text>}
        {type === 'services'
          ? nonServicesPaymentMethods?.map(method => (
              <SelectPaymentMethodCard
                key={method.id}
                paymentMethod={method}
                selected={selectedPaymentMethod?.id === method.id}
                onSelect={setSelectedPaymentMethod}
              />
            ))
          : bankPaymentMethods?.map(method => (
              <SelectPaymentMethodCard
                key={method.id}
                paymentMethod={method}
                selected={selectedPaymentMethod?.id === method.id}
                onSelect={setSelectedPaymentMethod}
              />
            ))}
        <Clickable onClick={onSelectAddNewPaymentMethod}>
          <Layout
            width="100%"
            height={68}
            padding={16}
            borderRadius={8}
            borderColor="gray-50"
            borderWidth={1}
            direction="row"
            align="center"
            justify="space-between"
          >
            <Layout direction="row" gap={12} align="center">
              <StripeBank />
              <Text size="body-md" semibold>
                Add new
              </Text>
            </Layout>
            <Icon name="plus" size="xl" color="purple-gradient" />
          </Layout>
        </Clickable>
        {showManagePaymentMethods && (
          <TextButton
            onClick={() => history.push(TENANT_ADMIN_PAYMENT_METHODS_PATH)}
            text="Manage payment methods"
            color="blue-500"
            textSize="body-sm"
            semibold={false}
          />
        )}
      </Layout>
    </Modal>
  );
};

export default UpdateStripePaymentMethodModal;
